import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Palette, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Top-level browseable grid of every coloring ebook.
 * Each card shows the book cover, page count, price, and a link
 * to the individual /coloring/:bookId page.
 */
export default function ColoringGallery() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    api.get("/books")
      .then(({ data }) => {
        if (cancelled) return;
        const withPages = (data || []).filter((b) => {
          const arr = Array.isArray(b.coloring_pages) ? b.coloring_pages : [];
          return arr.length > 0 || (b.coloring_page_url || "").trim();
        });
        setBooks(withPages);
      })
      .catch(() => { if (!cancelled) setBooks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (params.get("bundle_unlocked") === "1") {
      toast.success("The whole trio is yours 🎉 Every book is unlocked.");
    }
  }, [params]);

  const buyBundle = async () => {
    if (books.length < 2) return;
    setBundleBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "coloring-book-bundle",
        book_id: "bundle",
        origin_url: window.location.origin,
        bundle_book_ids: books.map((b) => b.id),
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("No checkout URL");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start checkout.");
      setBundleBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="coloring-gallery">
      <div className="max-w-2xl">
        <span
          className="inline-flex items-center gap-2 font-caveat text-3xl"
          style={{ color: "#FF6B9D" }}
        >
          <Palette size={22} /> printable coloring ebooks
        </span>
        <h1 className="font-fredoka text-5xl sm:text-6xl mt-1" style={{ color: "#2D3055" }}>
          A little coloring corner
        </h1>
        <p className="mt-3 font-nunito text-lg" style={{ color: "#4A4E6D" }}>
          Cozy, hand-drawn coloring ebooks that pair with M.K. Bunch's picture books.
          Peek at two pages free — unlock the whole book for $3.99.
        </p>
      </div>

      {loading && (
        <p className="mt-10 font-nunito" style={{ color: "#7E82A1" }}>Sharpening the crayons…</p>
      )}

      {!loading && books.length === 0 && (
        <p className="mt-10 font-caveat text-2xl" style={{ color: "#7E82A1" }}>
          No coloring ebooks yet — check back soon.
        </p>
      )}

      {!loading && books.length >= 2 && (
        <div
          className="mt-10 rounded-3xl p-6 flex items-center justify-between gap-4 flex-wrap"
          style={{
            background: "linear-gradient(135deg,#FFF3F8 0%,#FFF8E7 55%,#F0EBFF 100%)",
            border: "1.5px dashed #C7B5FF",
            boxShadow: "0 20px 50px rgba(107,71,199,0.10)",
          }}
          data-testid="coloring-gallery-bundle"
        >
          <div className="flex-1 min-w-[240px]">
            <p className="font-caveat text-2xl" style={{ color: "#6B47C7" }}>the sweetest deal</p>
            <h2 className="font-fredoka text-3xl leading-tight" style={{ color: "#2D3055" }}>
              Get the whole trio — <span style={{ color: "#FF6B9D" }}>$9.99</span>
            </h2>
            <p className="mt-1 font-nunito text-sm" style={{ color: "#4A4E6D" }}>
              All {books.length} coloring ebooks together. Instant PDF downloads, digital delivery only.
            </p>
          </div>
          <button
            type="button"
            onClick={buyBundle}
            disabled={bundleBusy}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm text-white transition-transform hover:-translate-y-0.5 disabled:opacity-70"
            style={{
              background: "linear-gradient(135deg,#6B47C7 0%,#FF6B9D 100%)",
              boxShadow: "0 12px 26px rgba(107,71,199,0.30)",
            }}
            data-testid="coloring-gallery-bundle-buy"
          >
            {bundleBusy ? (<><Loader2 size={16} className="animate-spin" /> Sending you to checkout…</>) : (<><Sparkles size={16} /> Unlock the trio for $9.99</>)}
          </button>
        </div>
      )}

      {!loading && books.length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((b, idx) => {
            const pageCount = (b.coloring_pages || []).filter(Boolean).length || 1;
            return (
              <Link
                key={b.id}
                to={`/coloring/${b.id}`}
                className="warm-card p-5 fade-up book-tilt relative"
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={`coloring-gallery-card-${b.id}`}
              >
                <div className="absolute top-3 right-3 z-10 rounded-full px-3 py-1 font-fredoka text-xs"
                     style={{ background: "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)", color: "#2D3055" }}>
                  $3.99
                </div>
                <div
                  className="rounded-2xl overflow-hidden mb-4 aspect-[3/4] flex items-center justify-center"
                  style={{ background: "linear-gradient(160deg,#FFF3F8 0%,#FFFFFF 60%,#F0EBFF 100%)" }}
                >
                  <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-contain" />
                </div>
                <p className="font-caveat text-lg" style={{ color: "#FF6B9D" }}>
                  {pageCount}-page coloring ebook
                </p>
                <h3 className="font-fredoka text-2xl mt-1" style={{ color: "#2D3055" }}>{b.title}</h3>
                <p className="mt-3 text-sm font-nunito inline-flex items-center gap-1" style={{ color: "#6EC4E8" }}>
                  Peek inside <ArrowRight size={14} />
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
